<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Draggable from 'vuedraggable';
import PlaybooksAPI from 'dashboard/api/playbooks';

import Button from 'dashboard/components-next/button/Button.vue';
import PlaybookDocumentRequests from './PlaybookDocumentRequests.vue';

const props = defineProps({
  playbookId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const PROCESS_GROUPS = [
  'initiating',
  'planning',
  'executing',
  'monitoring_controlling',
  'closing',
];
const STATUS_CATEGORIES = ['unstarted', 'started', 'done', 'canceled'];

const id = computed(() => Number(props.playbookId));

const playbook = computed(() => store.getters.getPlaybookById(id.value));

const isLoading = ref(false);
// Local, position-ordered copy of phases (each with a `stages` array).
const phases = ref([]);

// --- Phase form state ------------------------------------------------------
const isAddingPhase = ref(false);
const newPhaseName = ref('');
const newPhaseGroup = ref('initiating');
const isSavingPhase = ref(false);

const editingPhaseId = ref(null);
const editPhaseName = ref('');
const editPhaseGroup = ref('initiating');
const editPhaseGate = ref(false);

// --- Stage form state (scoped to a single phase at a time) -----------------
const addingStagePhaseId = ref(null);
const newStageName = ref('');
const newStageCategory = ref('unstarted');
const isSavingStage = ref(false);

const editingStageId = ref(null);
const editStageName = ref('');
const editStageCategory = ref('unstarted');

const sortedStages = stages =>
  [...(stages || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

const loadPhases = async () => {
  isLoading.value = true;
  try {
    const { data } = await PlaybooksAPI.getPhases(id.value);
    phases.value = (data || [])
      .map(p => ({ ...p, stages: sortedStages(p.stages) }))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const goBack = () => router.push({ name: 'playbooks_list' });

// --- Phase reorder ---------------------------------------------------------
const onPhaseReorder = async () => {
  const phaseIds = phases.value.map(p => p.id);
  try {
    await PlaybooksAPI.reorderPhases(id.value, phaseIds);
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.REORDER_ERROR'));
    await loadPhases();
  }
};

const movePhase = async (phase, direction) => {
  const ids = phases.value.map(p => p.id);
  const idx = ids.indexOf(phase.id);
  const target = idx + direction;
  if (target < 0 || target >= ids.length) return;
  [ids[idx], ids[target]] = [ids[target], ids[idx]];
  const next = ids.map(pid => phases.value.find(p => p.id === pid));
  phases.value = next;
  try {
    await PlaybooksAPI.reorderPhases(id.value, ids);
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.REORDER_ERROR'));
    await loadPhases();
  }
};

// --- Phase CRUD ------------------------------------------------------------
const startAddPhase = () => {
  isAddingPhase.value = true;
  newPhaseName.value = '';
  newPhaseGroup.value = 'initiating';
};

const cancelAddPhase = () => {
  isAddingPhase.value = false;
  newPhaseName.value = '';
};

const submitAddPhase = async () => {
  const name = newPhaseName.value.trim();
  if (!name || isSavingPhase.value) return;
  isSavingPhase.value = true;
  try {
    await PlaybooksAPI.createPhase(id.value, {
      name,
      process_group: newPhaseGroup.value,
    });
    cancelAddPhase();
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.SAVE_ERROR'));
  } finally {
    isSavingPhase.value = false;
  }
};

const startEditPhase = phase => {
  editingPhaseId.value = phase.id;
  editPhaseName.value = phase.name;
  editPhaseGroup.value = phase.process_group || 'initiating';
  editPhaseGate.value = !!phase.gate_required;
};

const cancelEditPhase = () => {
  editingPhaseId.value = null;
  editPhaseName.value = '';
};

const submitEditPhase = async phase => {
  const name = editPhaseName.value.trim();
  if (!name || isSavingPhase.value) return;
  isSavingPhase.value = true;
  try {
    await PlaybooksAPI.updatePhase(id.value, phase.id, {
      name,
      process_group: editPhaseGroup.value,
      gate_required: editPhaseGate.value,
    });
    cancelEditPhase();
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.SAVE_ERROR'));
  } finally {
    isSavingPhase.value = false;
  }
};

const deletePhase = async phase => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.PLAYBOOKS.PHASE.DELETE_CONFIRM'))) return;
  try {
    await PlaybooksAPI.deletePhase(id.value, phase.id);
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.PHASE.DELETE_ERROR'));
  }
};

// --- Stage CRUD ------------------------------------------------------------
const startAddStage = phase => {
  addingStagePhaseId.value = phase.id;
  newStageName.value = '';
  newStageCategory.value = 'unstarted';
};

const cancelAddStage = () => {
  addingStagePhaseId.value = null;
  newStageName.value = '';
};

const submitAddStage = async phase => {
  const name = newStageName.value.trim();
  if (!name || isSavingStage.value) return;
  isSavingStage.value = true;
  try {
    await PlaybooksAPI.createStage(id.value, phase.id, {
      name,
      status_category: newStageCategory.value,
    });
    cancelAddStage();
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.STAGE.SAVE_ERROR'));
  } finally {
    isSavingStage.value = false;
  }
};

const startEditStage = stage => {
  editingStageId.value = stage.id;
  editStageName.value = stage.name;
  editStageCategory.value = stage.status_category || 'unstarted';
};

const cancelEditStage = () => {
  editingStageId.value = null;
  editStageName.value = '';
};

const submitEditStage = async (phase, stage) => {
  const name = editStageName.value.trim();
  if (!name || isSavingStage.value) return;
  isSavingStage.value = true;
  try {
    await PlaybooksAPI.updateStage(id.value, phase.id, stage.id, {
      name,
      status_category: editStageCategory.value,
    });
    cancelEditStage();
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.STAGE.SAVE_ERROR'));
  } finally {
    isSavingStage.value = false;
  }
};

const deleteStage = async (phase, stage) => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('PROJECTS.PLAYBOOKS.STAGE.DELETE_CONFIRM'))) return;
  try {
    await PlaybooksAPI.deleteStage(id.value, phase.id, stage.id);
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.STAGE.DELETE_ERROR'));
  }
};

const moveStage = async (phase, stage, direction) => {
  const ids = sortedStages(phase.stages).map(s => s.id);
  const idx = ids.indexOf(stage.id);
  const target = idx + direction;
  if (target < 0 || target >= ids.length) return;
  [ids[idx], ids[target]] = [ids[target], ids[idx]];
  try {
    await PlaybooksAPI.reorderStages(id.value, phase.id, ids);
    await loadPhases();
  } catch (error) {
    useAlert(t('PROJECTS.PLAYBOOKS.STAGE.REORDER_ERROR'));
  }
};

onMounted(async () => {
  if (!playbook.value) {
    try {
      await store.dispatch('fetchPlaybook', { id: id.value });
    } catch (error) {
      // ignore; NOT_FOUND state handles missing playbook
    }
  }
  await loadPhases();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex items-center justify-between gap-3 px-6 pt-5 pb-3 border-b shrink-0 border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-8 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 shrink-0"
          :title="t('PROJECTS.PLAYBOOKS.BACK')"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div class="flex flex-col min-w-0">
          <h1 class="text-xl font-semibold truncate text-n-slate-12">
            {{ playbook?.name || t('PROJECTS.PLAYBOOKS.EDIT') }}
          </h1>
          <p v-if="playbook?.category" class="text-sm truncate text-n-slate-10">
            {{ playbook.category }}
          </p>
        </div>
      </div>
      <Button
        v-if="!isAddingPhase"
        :label="t('PROJECTS.PLAYBOOKS.PHASE.ADD')"
        icon="i-lucide-plus"
        size="sm"
        color="blue"
        @click="startAddPhase"
      />
    </header>

    <!-- Body -->
    <section class="flex-1 min-h-0 px-6 py-4 overflow-y-auto">
      <!-- Loading -->
      <div v-if="isLoading && !phases.length" class="flex flex-col gap-3">
        <div
          v-for="n in 3"
          :key="n"
          class="h-20 rounded-xl animate-pulse bg-n-alpha-1"
        />
      </div>

      <!-- Not found -->
      <div
        v-else-if="!playbook && !isLoading && !phases.length"
        class="flex flex-col items-center justify-center h-full gap-2 text-center"
      >
        <span
          class="flex items-center justify-center rounded-2xl size-12 bg-n-alpha-1 text-n-slate-10"
        >
          <span class="i-lucide-book-x size-6" />
        </span>
        <p class="text-sm text-n-slate-11">
          {{ t('PROJECTS.PLAYBOOKS.NOT_FOUND') }}
        </p>
      </div>

      <template v-else>
        <!-- Add-phase form -->
        <div
          v-if="isAddingPhase"
          class="flex flex-wrap items-center gap-2 p-3 mb-4 border rounded-xl border-n-weak bg-n-alpha-1/40"
        >
          <input
            v-model="newPhaseName"
            type="text"
            :placeholder="t('PROJECTS.PLAYBOOKS.PHASE.NAME_PLACEHOLDER')"
            class="h-8 px-3 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand min-w-[12rem]"
            @keyup.enter="submitAddPhase"
            @keyup.esc="cancelAddPhase"
          />
          <select
            v-model="newPhaseGroup"
            class="h-8 px-2 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
          >
            <option v-for="g in PROCESS_GROUPS" :key="g" :value="g">
              {{ t(`PROJECTS.PLAYBOOKS.PROCESS_GROUPS.${g.toUpperCase()}`) }}
            </option>
          </select>
          <Button
            size="sm"
            color="blue"
            :label="t('PROJECTS.PLAYBOOKS.ACTIONS.SAVE')"
            :is-loading="isSavingPhase"
            :disabled="!newPhaseName.trim()"
            @click="submitAddPhase"
          />
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            @click="cancelAddPhase"
          />
        </div>

        <!-- Empty -->
        <div
          v-if="!phases.length && !isAddingPhase"
          class="flex flex-col items-center justify-center gap-3 py-12 text-center"
        >
          <span
            class="flex items-center justify-center rounded-2xl size-12 bg-n-alpha-1 text-n-slate-10"
          >
            <span class="i-lucide-list-tree size-6" />
          </span>
          <p class="text-sm text-n-slate-11">
            {{ t('PROJECTS.PLAYBOOKS.PHASE.EMPTY') }}
          </p>
          <Button
            :label="t('PROJECTS.PLAYBOOKS.PHASE.ADD')"
            icon="i-lucide-plus"
            size="sm"
            variant="faded"
            color="slate"
            @click="startAddPhase"
          />
        </div>

        <!-- Phase list (draggable) -->
        <Draggable
          v-else
          v-model="phases"
          item-key="id"
          handle=".phase-drag"
          :animation="150"
          ghost-class="opacity-40"
          class="flex flex-col gap-3"
          @end="onPhaseReorder"
        >
          <template #item="{ element: phase, index: pIdx }">
            <div class="border rounded-xl border-n-weak bg-n-solid-1">
              <!-- Phase header -->
              <div
                class="flex items-center gap-2 px-3 py-2.5 border-b border-n-weak"
              >
                <span
                  class="flex items-center justify-center cursor-grab active:cursor-grabbing phase-drag text-n-slate-9 size-6 shrink-0"
                >
                  <span class="i-lucide-grip-vertical size-4" />
                </span>

                <!-- Edit mode -->
                <template v-if="editingPhaseId === phase.id">
                  <input
                    v-model="editPhaseName"
                    type="text"
                    class="h-8 px-2 text-sm border rounded-md reset-base bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand min-w-[10rem]"
                    @keyup.enter="submitEditPhase(phase)"
                    @keyup.esc="cancelEditPhase"
                  />
                  <select
                    v-model="editPhaseGroup"
                    class="h-8 px-2 text-sm border rounded-md reset-base bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
                  >
                    <option v-for="g in PROCESS_GROUPS" :key="g" :value="g">
                      {{
                        t(
                          `PROJECTS.PLAYBOOKS.PROCESS_GROUPS.${g.toUpperCase()}`
                        )
                      }}
                    </option>
                  </select>
                  <label
                    class="flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer"
                  >
                    <input
                      v-model="editPhaseGate"
                      type="checkbox"
                      class="rounded border-n-weak"
                    />
                    {{ t('PROJECTS.PLAYBOOKS.PHASE.GATE_REQUIRED') }}
                  </label>
                  <div class="flex items-center gap-1 ml-auto">
                    <Button
                      size="sm"
                      color="blue"
                      icon="i-lucide-check"
                      :is-loading="isSavingPhase"
                      @click="submitEditPhase(phase)"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      color="slate"
                      icon="i-lucide-x"
                      @click="cancelEditPhase"
                    />
                  </div>
                </template>

                <!-- View mode -->
                <template v-else>
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ phase.name }}
                  </span>
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 text-xs rounded-md shrink-0 bg-n-alpha-1 text-n-slate-11"
                  >
                    {{
                      t(
                        `PROJECTS.PLAYBOOKS.PROCESS_GROUPS.${(
                          phase.process_group || 'initiating'
                        ).toUpperCase()}`
                      )
                    }}
                  </span>
                  <span
                    v-if="phase.gate_required"
                    class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md shrink-0 bg-n-amber-3 text-n-amber-11"
                  >
                    <span class="i-lucide-shield-check size-3" />
                    {{ t('PROJECTS.PLAYBOOKS.PHASE.GATE_REQUIRED') }}
                  </span>

                  <div class="flex items-center gap-0.5 ml-auto">
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.PHASE.MOVE_UP')"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      icon="i-lucide-chevron-up"
                      :disabled="pIdx === 0"
                      @click="movePhase(phase, -1)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.PHASE.MOVE_DOWN')"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      icon="i-lucide-chevron-down"
                      :disabled="pIdx === phases.length - 1"
                      @click="movePhase(phase, 1)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.PHASE.EDIT')"
                      variant="ghost"
                      color="slate"
                      size="sm"
                      icon="i-lucide-pencil"
                      @click="startEditPhase(phase)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.PHASE.DELETE')"
                      variant="ghost"
                      color="ruby"
                      size="sm"
                      icon="i-lucide-trash-2"
                      @click="deletePhase(phase)"
                    />
                  </div>
                </template>
              </div>

              <!-- Stages -->
              <div class="flex flex-wrap items-center gap-2 px-3 py-2.5">
                <span class="text-xs font-medium text-n-slate-10 mr-1">
                  {{ t('PROJECTS.PLAYBOOKS.STAGES') }}
                </span>

                <template
                  v-for="(stage, sIdx) in sortedStages(phase.stages)"
                  :key="stage.id"
                >
                  <!-- Edit stage -->
                  <div
                    v-if="editingStageId === stage.id"
                    class="flex items-center gap-1.5 p-1 rounded-lg bg-n-alpha-1"
                  >
                    <input
                      v-model="editStageName"
                      type="text"
                      class="h-7 w-28 px-2 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
                      @keyup.enter="submitEditStage(phase, stage)"
                      @keyup.esc="cancelEditStage"
                    />
                    <select
                      v-model="editStageCategory"
                      class="h-7 px-1.5 text-xs border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
                    >
                      <option
                        v-for="c in STATUS_CATEGORIES"
                        :key="c"
                        :value="c"
                      >
                        {{
                          t(
                            `PROJECTS.PLAYBOOKS.STAGE.CATEGORY.${c.toUpperCase()}`
                          )
                        }}
                      </option>
                    </select>
                    <Button
                      size="sm"
                      color="blue"
                      icon="i-lucide-check"
                      :is-loading="isSavingStage"
                      @click="submitEditStage(phase, stage)"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      color="slate"
                      icon="i-lucide-x"
                      @click="cancelEditStage"
                    />
                  </div>

                  <!-- View stage -->
                  <div
                    v-else
                    class="flex items-center gap-0.5 pl-2.5 pr-1 h-8 rounded-lg group bg-n-alpha-1"
                  >
                    <span class="text-sm text-n-slate-12">{{
                      stage.name
                    }}</span>
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.STAGE.MOVE_LEFT')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      icon="i-lucide-chevron-left"
                      :disabled="sIdx === 0"
                      @click="moveStage(phase, stage, -1)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.STAGE.MOVE_RIGHT')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      icon="i-lucide-chevron-right"
                      :disabled="sIdx === sortedStages(phase.stages).length - 1"
                      @click="moveStage(phase, stage, 1)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.STAGE.EDIT')"
                      variant="ghost"
                      color="slate"
                      size="xs"
                      icon="i-lucide-pencil"
                      @click="startEditStage(stage)"
                    />
                    <Button
                      v-tooltip.top="t('PROJECTS.PLAYBOOKS.STAGE.DELETE')"
                      variant="ghost"
                      color="ruby"
                      size="xs"
                      icon="i-lucide-trash-2"
                      @click="deleteStage(phase, stage)"
                    />
                  </div>
                </template>

                <!-- Add stage -->
                <div
                  v-if="addingStagePhaseId === phase.id"
                  class="flex items-center gap-1.5 p-1 rounded-lg bg-n-alpha-1"
                >
                  <input
                    v-model="newStageName"
                    type="text"
                    :placeholder="
                      t('PROJECTS.PLAYBOOKS.STAGE.NAME_PLACEHOLDER')
                    "
                    class="h-7 w-28 px-2 text-sm border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                    @keyup.enter="submitAddStage(phase)"
                    @keyup.esc="cancelAddStage"
                  />
                  <select
                    v-model="newStageCategory"
                    class="h-7 px-1.5 text-xs border rounded-md reset-base bg-n-solid-1 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
                  >
                    <option v-for="c in STATUS_CATEGORIES" :key="c" :value="c">
                      {{
                        t(
                          `PROJECTS.PLAYBOOKS.STAGE.CATEGORY.${c.toUpperCase()}`
                        )
                      }}
                    </option>
                  </select>
                  <Button
                    size="sm"
                    color="blue"
                    :label="t('PROJECTS.PLAYBOOKS.ACTIONS.SAVE')"
                    :is-loading="isSavingStage"
                    :disabled="!newStageName.trim()"
                    @click="submitAddStage(phase)"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="slate"
                    icon="i-lucide-x"
                    @click="cancelAddStage"
                  />
                </div>
                <Button
                  v-else
                  size="sm"
                  variant="faded"
                  color="slate"
                  icon="i-lucide-plus"
                  :label="t('PROJECTS.PLAYBOOKS.STAGE.ADD')"
                  @click="startAddStage(phase)"
                />
              </div>
            </div>
          </template>
        </Draggable>
      </template>

      <div class="pt-6 mt-6 border-t border-n-weak">
        <h2 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ t('PROJECTS.PLAYBOOKS.DOCUMENT_REQUESTS_TITLE') }}
        </h2>
        <PlaybookDocumentRequests :playbook-id="id" />
      </div>
    </section>
  </div>
</template>
